import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { AppContext } from "../../../App";
import { HelpContent } from "../../shared/help";
import { objectVals2String } from "../../../utils/objectVals2String";
import { trimOnBlur } from "../../../utils/trimOnBlur";

interface Values {
  answer: string;
  followUpAnswer: string;
}

export type RadioOption = {
  value: string;
  label?: string;
  response?: string | JSX.Element;
  help?: HelpContent;
  followUp?: string;
};

interface Props {
  initAnswer?: string;
  initMoreAnswer?: string;
  answerLabel?: string;
  errorText?: string;
  options: RadioOption[];
  onComplete: (answer: string, followUpAnswer?: string) => Promise<boolean>;
  onBack?: () => void;
  centerButtons?: boolean;
}

export const RadioAndResponseForm = ({
  initAnswer,
  initMoreAnswer,
  answerLabel,
  errorText,
  options,
  onComplete,
  onBack,
  centerButtons,
}: Props): JSX.Element => {
  const { setHelpContent } = useContext(AppContext);

  const optionsRequiringFollowUp: string[] = [];
  options.forEach((option: RadioOption) => {
    option.followUp && optionsRequiringFollowUp.push(option.value);
  });

  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      answer: initAnswer || "",
      followUpAnswer: initMoreAnswer || "",
    },
    schema: Yup.object().shape({
      answer: Yup.string().required(errorText || `${answerLabel} is required`),
      followUpAnswer: Yup.string().when("answer", (answer) => {
        return optionsRequiringFollowUp.length &&
          optionsRequiringFollowUp.includes(answer)
          ? Yup.string().required()
          : Yup.string();
      }),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      await onComplete(values.answer, values.followUpAnswer);
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      validationSchema={formikInit.schema}
      onSubmit={handleSubmit}
      // force re-render by effectively subscribing to specific value
      key={objectVals2String(formikInit.values)}
    >
      {({
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        status,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12}>
              <RadioGroup
                aria-label={answerLabel}
                name="answer"
                value={values.answer}
                // onBlur={handleBlur}
                onChange={handleChange}
                // error={Boolean(touched.answer && errors.answer)}
              >
                {options.map((option: RadioOption) => {
                  return (
                    <React.Fragment key={`option-${option.value}`}>
                      <Box>
                        <FormControlLabel
                          value={option.value}
                          control={<Radio />}
                          label={option.label || option.value}
                          sx={{ mr: 0, my: "4px" }}
                        />
                        {option.help && (
                          <IconButton
                            onClick={() => {
                              if (option.help) {
                                if (option.help.href) {
                                  window.open(option.help.href);
                                } else {
                                  setHelpContent(option.help);
                                }
                              }
                            }}
                            aria-label="help"
                            color="secondary"
                          >
                            <HelpOutlineOutlinedIcon />
                          </IconButton>
                        )}
                      </Box>
                      {values.answer === option.value && option.followUp && (
                        <TextField
                          error={Boolean(
                            touched.followUpAnswer && errors.followUpAnswer
                          )}
                          label={option.followUp}
                          name="followUpAnswer"
                          onBlur={(evt: React.FocusEvent<any>) => {
                            trimOnBlur(
                              evt,
                              "followUpAnswer",
                              setFieldValue,
                              handleBlur
                            );
                          }}
                          onChange={handleChange}
                          type="text"
                          value={values.followUpAnswer}
                          variant="outlined"
                          sx={{
                            my: 2,
                          }}
                        />
                      )}
                      {values.answer === option.value && option.response && (
                        <Typography
                          sx={{
                            my: 2,
                          }}
                        >
                          {option.response}
                        </Typography>
                      )}
                    </React.Fragment>
                  );
                })}
              </RadioGroup>
              {errors.answer && (
                <Typography
                  variant="body1"
                  color="error"
                  sx={{
                    my: 2,
                  }}
                >
                  {errors.answer}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                onBack={onBack}
                centered={centerButtons}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
